"use client";

import { NPSInline, type NPSInlineStep } from "@/components/ui/nps-inline";
import { useNPSTimer } from "@/hooks/useNPSTimer";
import {
  captureNpsScoreSubmitted,
  captureNpsSurveyCompleted,
  captureNpsSurveyDismissed,
  captureNpsSurveyShown,
} from "@/lib/analytics/nps-events";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { getNpsLastGame } from "@/lib/nps/session";
import { XIcon } from "@phosphor-icons/react";
import { usePathname } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";

async function submitNpsScore(
  gameSlug: string,
  gameId: string | null | undefined,
  score: number
): Promise<{ id: string }> {
  const response = await fetch("/api/nps", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      score,
      game_slug: gameSlug,
      game_id: gameId ?? undefined,
    }),
  });

  const payload = (await response.json().catch(() => null)) as {
    data?: { id?: string };
    error?: string;
  } | null;

  const id = payload?.data?.id;
  if (!response.ok || typeof id !== "string" || !id) {
    console.error("NPS score submission failed", {
      status: response.status,
      gameSlug,
      score,
      error: payload?.error,
      responseId: id,
    });
    throw new Error(payload?.error || "Failed to save score.");
  }

  return { id };
}

async function submitNpsFeedback(responseId: string, feedback: string): Promise<void> {
  const response = await fetch("/api/nps", {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      id: responseId,
      feedback,
    }),
  });

  const payload = (await response.json().catch(() => null)) as {
    error?: string;
  } | null;

  if (!response.ok) {
    console.error("NPS feedback submission failed", {
      status: response.status,
      responseId,
      error: payload?.error,
    });
    throw new Error(payload?.error || "Failed to save feedback.");
  }
}

function isGameTheaterPath(pathname: string | null): boolean {
  if (!pathname) return false;
  // Direct game page or intercepting overlay: /games/[slug]
  return /^\/games\/[^/]+\/?$/.test(pathname);
}

/**
 * Site-wide sticky NPS after the dwell timer, shown only when the visitor is
 * not inside the game theater — so the form never covers gameplay.
 */
export function StickyNPSSurvey() {
  const pathname = usePathname();
  const posthog = usePostHog();
  const { shouldShowNPS, dismissNPS } = useNPSTimer();
  const lastGame = getNpsLastGame();
  const [step, setStep] = useState<NPSInlineStep>("rate");
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const shownForSlugRef = useRef<string | null>(null);

  const inTheater = isGameTheaterPath(pathname);
  const visible = shouldShowNPS && !inTheater && Boolean(lastGame?.slug);

  useEffect(() => {
    if (!visible || !lastGame?.slug) {
      if (!shouldShowNPS) {
        shownForSlugRef.current = null;
        setStep("rate");
        setSelectedScore(null);
      }
      return;
    }
    if (shownForSlugRef.current === lastGame.slug) return;
    shownForSlugRef.current = lastGame.slug;
    captureNpsSurveyShown(posthog?.capture.bind(posthog), lastGame.slug);
  }, [visible, shouldShowNPS, lastGame?.slug, posthog]);

  const handleStepChange = useCallback((next: NPSInlineStep) => {
    setStep(next);
  }, []);

  const handleDismissClick = () => {
    if (!lastGame?.slug) {
      dismissNPS();
      return;
    }
    const dismissStage = step === "feedback" ? "after_score" : "before_score";
    captureNpsSurveyDismissed(posthog?.capture.bind(posthog), lastGame.slug, dismissStage, {
      score: selectedScore,
    });
    dismissNPS();
  };

  const handleComplete = (details: { score: number; hasFeedback: boolean }) => {
    if (lastGame?.slug) {
      captureNpsSurveyCompleted(posthog?.capture.bind(posthog), lastGame.slug, {
        score: details.score,
        has_feedback: details.hasFeedback,
      });
    }
    dismissNPS();
  };

  if (!visible || !lastGame?.slug) {
    return null;
  }

  const dismissButtonClassName =
    "inline-flex h-7 w-7 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors";

  return (
    <aside
      data-testid="nps-survey"
      className={`fixed inset-x-0 bottom-0 ${Z_INDEX_CLASSES.GAME_NPS_OVERLAY} border-t border-gray-200 bg-white shadow-[0_-8px_24px_rgba(0,0,0,0.12)]`}
      aria-label="Feedback survey"
    >
      {/* Mobile: dismiss on its own top-right row, away from score controls */}
      {step !== "thankyou" ? (
        <div className="flex justify-end px-2 pt-1.5 sm:hidden">
          <button
            type="button"
            onClick={handleDismissClick}
            className={dismissButtonClassName}
            aria-label="Dismiss feedback survey"
            data-testid="nps-dismiss"
          >
            <XIcon weight="bold" size={14} />
          </button>
        </div>
      ) : null}
      {/* Desktop: vertically centered on the far left */}
      {step !== "thankyou" ? (
        <button
          type="button"
          onClick={handleDismissClick}
          className={`absolute left-2 top-1/2 hidden -translate-y-1/2 sm:inline-flex ${dismissButtonClassName}`}
          aria-label="Dismiss feedback survey"
          data-testid="nps-dismiss-desktop"
        >
          <XIcon weight="bold" size={14} />
        </button>
      ) : null}
      <div className="mx-auto w-full max-w-3xl px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-1 sm:pl-12 sm:pr-4 sm:py-4">
        <NPSInline
          onSubmitScore={async (score) => {
            setSelectedScore(score);
            const result = await submitNpsScore(lastGame.slug, lastGame.id, score);
            captureNpsScoreSubmitted(posthog?.capture.bind(posthog), lastGame.slug, score);
            return result;
          }}
          onSubmitFeedback={submitNpsFeedback}
          onComplete={handleComplete}
          onStepChange={handleStepChange}
        />
      </div>
    </aside>
  );
}
